import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useLocalization from "../../hooks/useLocalization";
import { buildSpacesImageUrl } from "../../utils/spacesImageUrl";
import useApi from "../../hooks/useApi";

const QAImageGallery = ({
  images,
  imageApprovals,
  onApprove,
  onUnapprove,
  onToggleReviewFlag,
  onReplace,
  onRotate,
  answer,
  questionType,
}) => {
  const localization = useLocalization();
  const { retrieveFile } = useApi();
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [hiddenImageKeys, setHiddenImageKeys] = useState({});
  const [resolvedImageUrls, setResolvedImageUrls] = useState({});

  const imageVersionToken =
    answer?.updatedAt || answer?.qaReviewedAt || answer?.createdAt || "";

  const withCacheBust = (url) => {
    if (!url || !imageVersionToken) return url;

    const token = encodeURIComponent(String(imageVersionToken));
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}v=${token}`;
  };

  const getImageKey = (image) => {
    if (!image) return null;
    if (typeof image === "string") return image;
    if (typeof image === "object") {
      return (
        image.fileName ||
        image.imageKey ||
        image.key ||
        image.name ||
        image.data ||
        image.url ||
        null
      );
    }
    return String(image);
  };

  const getComparableKeys = (value) => {
    if (!value) return [];
    const keys = [value];
    if (typeof value === "string" && /^(https?:)/i.test(value)) {
      try {
        const parsed = new URL(value);
        const path = parsed.pathname.replace(/^\/+/, "");
        const base = path.split("/").pop();
        if (path) keys.push(path);
        if (base) keys.push(base);
      } catch {
        // Ignore malformed URL strings.
      }
    }
    if (typeof value === "string" && value.includes("/")) {
      const base = value.split("/").pop();
      if (base) keys.push(base);
    }
    return Array.from(new Set(keys.filter(Boolean)));
  };

  // Normalize images to array (handle case where it might be an object)
  const normalizedImages = Array.isArray(images) ? images : [];

  useEffect(() => {
    let isMounted = true;

    const loadUrls = async () => {
      const keys = normalizedImages
        .map((img) => getImageKey(img))
        .filter(Boolean)
        .filter((key) => !(key in resolvedImageUrls));

      if (keys.length === 0) return;

      const results = await Promise.all(
        keys.map(async (key) => {
          try {
            const url = await retrieveFile(key);
            return { key, url };
          } catch {
            return { key, url: null };
          }
        }),
      );

      if (!isMounted) return;

      setResolvedImageUrls((prev) => {
        const next = { ...prev };
        for (const entry of results) {
          next[entry.key] = entry.url;
        }
        return next;
      });
    };

    loadUrls();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedImages]);

  // Find approval status for image by matching filename
  const getImageApproval = (image) => {
    const filename = getImageKey(image);
    const comparableKeys = getComparableKeys(filename);
    return imageApprovals.find((a) => comparableKeys.includes(a?.imageKey));
  };

  const getImageStatus = (image) => {
    const approval = getImageApproval(image);
    return approval ? approval.status : "pending";
  };

  const isReviewRequested = (image) => {
    const approval = getImageApproval(image);
    return approval?.reviewRequested === true;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "unapproved":
        return "error";
      case "pending":
      default:
        return "warning";
    }
  };

  // Helper function to determine if a filename is an entry photo or action photo
  const getPhotoType = (filename) => {
    if (!answer?.value) return null;

    // Check if it's in entryPhoto array
    if (answer.value.entryPhoto?.some((p) => getImageKey(p) === filename)) {
      return "entry";
    }

    // Check if it's in actionPhoto array
    if (answer.value.actionPhoto?.some((p) => getImageKey(p) === filename)) {
      return "action";
    }

    return null;
  };

  // Helper function to get action label from localization
  const getActionLabel = (actionId) => {
    if (!actionId || !localization.distribution?.actionTaken) return "";
    const action = localization.distribution.actionTaken.find(
      (a) => a.id === actionId,
    );
    return action?.name || actionId;
  };

  // Helper function to get follow-up question label
  const getFollowUpQuestionLabel = (questionType) => {
    if (!questionType || !localization.distribution?.followUpQuestions)
      return "";
    return (
      localization.distribution.followUpQuestions[questionType] || questionType
    );
  };

  // Helper function to get follow-up answer label
  const getFollowUpAnswerLabel = (questionType, answerId) => {
    if (!questionType || !localization.distribution?.followUpOptions) return "";

    const options =
      localization.distribution.followUpOptions[questionType] || [];
    const option = options.find((o) => o.id === answerId);

    if (!option) return answerId;
    if (answerId === "other") return `${option.name} (comment)`;
    return option.name;
  };

  if (!normalizedImages || normalizedImages.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: "center" }}>
        <Typography color="textSecondary">No images</Typography>
      </Paper>
    );
  }

  const selectedImage =
    selectedImageIndex !== null ? normalizedImages[selectedImageIndex] : null;
  const selectedImageKey = selectedImage ? getImageKey(selectedImage) : null;
  const selectedImageUrlRaw = selectedImage
    ? resolvedImageUrls[selectedImageKey] ||
      buildSpacesImageUrl(selectedImageKey, {
        defaultFolder: "survey-images",
      })
    : null;
  const selectedImageUrl = withCacheBust(selectedImageUrlRaw);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflow: "visible",
        }}
      >
        {normalizedImages.map((image, index) => {
          const filename = getImageKey(image);
          if (!filename) return null;
          if (hiddenImageKeys[filename]) return null;

          const status = getImageStatus(image);
          const imageUrlRaw =
            resolvedImageUrls[filename] ||
            buildSpacesImageUrl(filename, {
              defaultFolder: "survey-images",
            });
          if (resolvedImageUrls[filename] === null && !imageUrlRaw) return null;
          const imageUrl = withCacheBust(imageUrlRaw);

          const photoType = getPhotoType(filename);
          const isEntryPhoto = photoType === "entry";
          const isActionPhoto = photoType === "action";
          const actionId = answer?.value?.action;
          const followUpData = answer?.value?.followUp;
          const availableForSale = answer?.value?.availableForSale;
          const isDistributionQuestion = questionType === "distribution";
          const isInDistribution =
            availableForSale === 1 ||
            availableForSale === "1" ||
            availableForSale === true;
          const statusLabel = isDistributionQuestion
            ? isInDistribution
              ? "In Distribution (YES)"
              : "Not In Distribution (NO)"
            : localization.questionTypeLabel(questionType);

          return (
            <Paper
              key={index}
              sx={{
                width: "100%",
                p: 2,
                backgroundColor: "background.default",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 1.5, alignItems: "center" }}
              >
                <Typography variant="subtitle2">Image {index + 1}</Typography>
                <Chip
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                  size="small"
                  color={getStatusColor(status)}
                  variant="filled"
                />
                {isReviewRequested(image) && (
                  <Chip
                    label="Review Requested"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Stack>

              {imageUrl ? (
                <Box
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    justifyContent: "center",
                    height: 220,
                    overflow: "hidden",
                    borderRadius: 1,
                    backgroundColor: "#f5f5f5",
                    cursor: "pointer",
                    "&:hover": {
                      opacity: 0.9,
                    },
                  }}
                  onClick={() => setSelectedImageIndex(index)}
                >
                  <img
                    src={imageUrl}
                    alt={`Image ${index + 1}`}
                    onError={() => {
                      setHiddenImageKeys((prev) => ({
                        ...prev,
                        [filename]: true,
                      }));
                    }}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "220px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    mb: 1.5,
                    p: 3,
                    textAlign: "center",
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}

              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", rowGap: 1, mb: 1.5 }}
              >
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => onApprove(filename)}
                >
                  Approve
                </Button>
                {status !== "unapproved" && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => onUnapprove(filename)}
                  >
                    Unapprove
                  </Button>
                )}
                {onToggleReviewFlag && (
                  <Button
                    variant={
                      isReviewRequested(image) ? "contained" : "outlined"
                    }
                    color="warning"
                    size="small"
                    onClick={() =>
                      onToggleReviewFlag(filename, !isReviewRequested(image))
                    }
                  >
                    {isReviewRequested(image)
                      ? "Clear Review Flag"
                      : "Request Review"}
                  </Button>
                )}
                {onReplace && (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => onReplace(index, filename, filename)}
                  >
                    Replace
                  </Button>
                )}
                {onRotate && (
                  <>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={() => onRotate(index, filename, -90)}
                    >
                      Rotate Left
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={() => onRotate(index, filename, 90)}
                    >
                      Rotate Right
                    </Button>
                  </>
                )}
              </Stack>

              <Paper
                sx={{
                  p: 1.5,
                  backgroundColor: "#fafafa",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, display: "block", mb: 1 }}
                >
                  Image Context
                </Typography>

                <Stack spacing={1}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: "textSecondary" }}
                    >
                      {isDistributionQuestion ? "Status" : "Question Type"}
                    </Typography>
                    <Chip
                      label={statusLabel}
                      size="small"
                      color={
                        isDistributionQuestion
                          ? isInDistribution
                            ? "success"
                            : "error"
                          : "default"
                      }
                      sx={{ mt: 0.5 }}
                    />
                  </Box>

                  {photoType && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: "textSecondary" }}
                      >
                        Photo Type
                      </Typography>
                      <Chip
                        label={isEntryPhoto ? "Entry Photo" : "Action Photo"}
                        size="small"
                        color={isEntryPhoto ? "primary" : "secondary"}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  )}

                  {isActionPhoto && actionId && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: "textSecondary" }}
                      >
                        Action Taken
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5 }}
                      >
                        {getActionLabel(actionId)}
                      </Typography>
                    </Box>
                  )}

                  {followUpData && (
                    <Box sx={{ pt: 1, borderTop: "1px solid #e0e0e0" }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: "textSecondary" }}
                      >
                        Follow-up Question
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5 }}
                      >
                        {getFollowUpQuestionLabel(followUpData.question)}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: "textSecondary",
                          display: "block",
                          mt: 1,
                        }}
                      >
                        Answer
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5 }}
                      >
                        {getFollowUpAnswerLabel(
                          followUpData.question,
                          followUpData.answer,
                        )}
                      </Typography>

                      {followUpData.answer === "other" &&
                        followUpData.comment && (
                          <>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color: "textSecondary",
                                display: "block",
                                mt: 1,
                              }}
                            >
                              Comment
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ mt: 0.5, fontStyle: "italic" }}
                            >
                              "{followUpData.comment}"
                            </Typography>
                          </>
                        )}
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Paper>
          );
        })}
      </Box>

      {/* Full-screen image modal */}
      <Dialog
        open={selectedImageIndex !== null}
        onClose={() => setSelectedImageIndex(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.95)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80vh",
          }}
        >
          <IconButton
            onClick={() => setSelectedImageIndex(null)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "white",
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>

          {selectedImageUrl && (
            <img
              src={selectedImageUrl}
              alt="Full size"
              style={{
                maxWidth: "100%",
                maxHeight: "85vh",
                objectFit: "contain",
              }}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default QAImageGallery;
